/**
 * Node Load Manager
 * Subscription-based progress tracking for batch geometry loading
 * Pattern based on Azure provider download manager
 */

import type {
  NodeSelectionEntry,
  LoadProgressSnapshot,
  LoadResult,
  LoadCompleteEvent,
  NodeGeometryFeature,
  NodeWithFeatures,
} from '../types';
import type { LoadSEERApiClient } from '../api';

type ProgressCallback = (snapshot: LoadProgressSnapshot) => void;
type CompleteCallback = (event: LoadCompleteEvent) => void;

export class NodeLoadManager {
  private apiClient: LoadSEERApiClient;
  private progressSubscribers = new Set<ProgressCallback>();
  private completeSubscribers = new Set<CompleteCallback>();
  private abortController: AbortController | null = null;
  private isLoading = false;

  // Progress tracking state
  private totalGeometryLoads = 0;
  private completedGeometryLoads = 0;
  private failedGeometryLoads = 0;

  constructor(apiClient: LoadSEERApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Subscribes to progress updates
   * Returns an unsubscribe function
   */
  subscribeProgress(callback: ProgressCallback): () => void {
    this.progressSubscribers.add(callback);
    return () => {
      this.progressSubscribers.delete(callback);
    };
  }

  /**
   * Subscribes to completion events
   * Returns an unsubscribe function
   */
  subscribeComplete(callback: CompleteCallback): () => void {
    this.completeSubscribers.add(callback);
    return () => {
      this.completeSubscribers.delete(callback);
    };
  }

  /**
   * Notifies all progress subscribers
   */
  private notifyProgress(snapshot: LoadProgressSnapshot): void {
    this.progressSubscribers.forEach(callback => {
      try {
        callback(snapshot);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error in progress callback:', error);
        }
      }
    });
  }

  /**
   * Notifies all complete subscribers
   */
  private notifyComplete(event: LoadCompleteEvent): void {
    this.completeSubscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error in complete callback:', error);
        }
      }
    });
  }

  /**
   * Loads geometries for all selected nodes
   * Handles both direct node geometries and descendant loading
   */
  async loadNodes(entries: NodeSelectionEntry[]): Promise<LoadResult[]> {
    if (this.isLoading) {
      throw new Error('Load operation already in progress');
    }

    this.isLoading = true;
    this.abortController = new AbortController();

    // Reset progress counters
    this.totalGeometryLoads = 0;
    this.completedGeometryLoads = 0;
    this.failedGeometryLoads = 0;

    const results: LoadResult[] = [];

    try {
      // Phase 1: Calculate total geometry loads including descendants
      this.notifyProgress({
        current: 0,
        total: entries.length,
        completed: 0,
        failed: 0,
        currentNodeName: 'Counting descendants...',
        phase: 'loading',
      });

      // Count total geometry loads (parent + descendants for each entry)
      for (const entry of entries) {
        this.totalGeometryLoads++; // Parent node geometry

        if (entry.includeDescendants) {
          try {
            const descendants = await this.apiClient.getAllDescendants(
              entry.lsid,
              entry.descendantNodeType
            );
            this.totalGeometryLoads += descendants.results.length;
          } catch (error) {
            if (import.meta.env.DEV) {
              console.error(`Failed to count descendants for ${entry.name}:`, error);
            }
          }
        }
      }

      // Phase 2: Load all geometries with accurate progress
      const batchSize = 10;
      for (let i = 0; i < entries.length; i += batchSize) {
        if (this.abortController.signal.aborted) {
          break;
        }

        const batch = entries.slice(i, i + batchSize);
        const batchPromises = batch.map(entry => this.loadNodeWithDescendants(entry));

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }

      // Final progress update
      this.notifyProgress({
        current: this.completedGeometryLoads + this.failedGeometryLoads,
        total: this.totalGeometryLoads,
        completed: this.completedGeometryLoads,
        failed: this.failedGeometryLoads,
        currentNodeName: '',
        phase: 'complete',
      });

      // Calculate total geometries and success/failure counts
      const totalGeometries = results.reduce((sum, result) => {
        return sum + (result.features?.length || 0);
      }, 0);

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      // Notify completion
      this.notifyComplete({
        results,
        successCount,
        failureCount,
        totalGeometries,
      });

      return results;
    } finally {
      this.isLoading = false;
      this.abortController = null;
    }
  }

  /**
   * Loads a single node with optional descendants
   */
  private async loadNodeWithDescendants(entry: NodeSelectionEntry): Promise<LoadResult> {
    try {
      const features: NodeGeometryFeature[] = [];
      const descendantNodes: NodeWithFeatures[] = [];
      const failedDescendants: Array<{ name: string; lsid: string; error: string }> = [];
      let descendantCount = 0;

      // Load the node's own geometry
      try {
        this.notifyProgress({
          current: this.completedGeometryLoads + this.failedGeometryLoads,
          total: this.totalGeometryLoads,
          completed: this.completedGeometryLoads,
          failed: this.failedGeometryLoads,
          currentNodeName: entry.name,
          phase: 'loading',
        });

        const nodeFeatures = await this.apiClient.getNodeGeometry(entry.lsid);
        features.push(...nodeFeatures);
        this.completedGeometryLoads++;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error(`Failed to load geometry for ${entry.name}:`, error);
        }
        this.failedGeometryLoads++;
        return {
          lsid: entry.lsid,
          name: entry.name,
          nodeType: entry.nodeType,
          externalId: entry.externalId,
          success: false,
          error: error instanceof Error ? error.message : 'Failed to load geometry',
        };
      }

      // Load descendants if requested
      if (entry.includeDescendants) {
        try {
          if (import.meta.env.DEV) {
            console.log(`Loading descendants for ${entry.name}:`, {
              lsid: entry.lsid,
              descendantNodeType: entry.descendantNodeType,
            });
          }
          const descendants = await this.apiClient.getAllDescendants(
            entry.lsid,
            entry.descendantNodeType
          );

          if (import.meta.env.DEV) {
            console.log(`Found ${descendants.results.length} descendants for ${entry.name}`);
          }
          descendantCount = descendants.results.length;

          // Load geometry for each descendant (with concurrency control)
          const descendantBatchSize = 10;
          for (let i = 0; i < descendants.results.length; i += descendantBatchSize) {
            if (this.abortController?.signal.aborted) {
              break;
            }

            const batch = descendants.results.slice(i, i + descendantBatchSize);
            const batchPromises = batch.map(async descendant => {
              try {
                const descendantFeatures = await this.apiClient.getNodeGeometry(descendant.lsid);
                this.completedGeometryLoads++;

                // Update progress after each successful load
                this.notifyProgress({
                  current: this.completedGeometryLoads + this.failedGeometryLoads,
                  total: this.totalGeometryLoads,
                  completed: this.completedGeometryLoads,
                  failed: this.failedGeometryLoads,
                  currentNodeName: descendant.name,
                  phase: 'loading',
                });

                return {
                  success: true,
                  node: descendant,
                  features: descendantFeatures,
                };
              } catch (error) {
                const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                if (import.meta.env.DEV) {
                  console.error(`Failed to load descendant ${descendant.name}:`, error);
                }
                this.failedGeometryLoads++;

                // Update progress after each failed load
                this.notifyProgress({
                  current: this.completedGeometryLoads + this.failedGeometryLoads,
                  total: this.totalGeometryLoads,
                  completed: this.completedGeometryLoads,
                  failed: this.failedGeometryLoads,
                  currentNodeName: descendant.name,
                  phase: 'loading',
                });

                return {
                  success: false,
                  node: descendant,
                  error: errorMsg,
                };
              }
            });

            const batchResults = await Promise.all(batchPromises);
            batchResults.forEach(result => {
              if (result.success) {
                descendantNodes.push({
                  lsid: result.node.lsid,
                  name: result.node.name,
                  nodeType: result.node.nodeType,
                  externalId: result.node.externalId,
                  features: result.features!,
                  parentNodeName: entry.name,
                });
              } else {
                failedDescendants.push({
                  name: result.node.name,
                  lsid: result.node.lsid,
                  error: result.error!,
                });
              }
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error(`Failed to load descendants for ${entry.name}:`, error);
          }
          // Don't fail the entire operation if only descendants failed
        }
      }

      return {
        lsid: entry.lsid,
        name: entry.name,
        nodeType: entry.nodeType,
        externalId: entry.externalId,
        success: true,
        features,
        descendantNodes,
        descendantCount,
        failedDescendants: failedDescendants.length > 0 ? failedDescendants : undefined,
      };
    } catch (error) {
      return {
        lsid: entry.lsid,
        name: entry.name,
        nodeType: entry.nodeType,
        externalId: entry.externalId,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Cancels the current load operation
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Checks if a load operation is in progress
   */
  isLoadingActive(): boolean {
    return this.isLoading;
  }
}
